import type { MiniAction } from '@otto-ec/tracking-bct';
import { log } from '@shared/log.browser.ts';
import type { TrackingGateway } from '@shared/tracking/Tracking.browser.ts';
import type { SearchContext } from '@shared/types/types.ts';
import type { BlackFridaySliderPort } from '@tilelist/application/ports/black-friday-slider/BlackFridaySliderPort.ts';
import type { SelectionEvents } from '@tilelist/application/ports/SelectionEvents.ts';
import { COLLAPSED_BLACK_FRIDAY_SLIDER } from '@tilelist/domain/black-friday-slider/BlackFridaySlider.ts';
import {
    BLACK_FRIDAY_SLIDER_FEATURE_ID,
    blackFridaySliderClickLabels,
    blackFridaySliderFeatures,
    blackFridaySliderLoadedLabels,
    sliderTileFeatureId,
} from '@tilelist/domain/black-friday-slider/BlackFridaySliderTracking.ts';
import type { BlackFridaySliderItem, ProductItem } from '@tilelist/domain/composition/TileListitem.ts';
import { productClickLabels } from '@tilelist/services/tracking/ProductClickLabels.browser.ts';

export class BlackFridaySliderService implements BlackFridaySliderPort {
    private readonly reportedTiles = new Set<number>();
    private reportedSlider = false;

    constructor(
        private readonly loader: Pick<BlackFridaySliderPort, 'load'>,
        private readonly gateway: TrackingGateway,
        private readonly selectionEvents: Pick<SelectionEvents, 'current'>,
    ) {}

    async load(searchContext: SearchContext): Promise<BlackFridaySliderItem> {
        let slider: BlackFridaySliderItem;
        try {
            slider = await this.loader.load(searchContext);
        } catch {
            slider = COLLAPSED_BLACK_FRIDAY_SLIDER;
        }
        this.reset();
        this.gateway.addToPageImpression(blackFridaySliderLoadedLabels(slider), blackFridaySliderFeatures(slider));
        return slider;
    }

    startPageImpression(): void {
        this.reset();
    }

    tilesShown(tiles: ProductItem[]): void {
        const miniActions = [...this.unreportedSliderActions(), ...this.unreportedTileActions(tiles)];
        if (miniActions.length === 0) return;

        log.debug(`trackBlackFridaySlider -- miniActions ${JSON.stringify(miniActions)}`);
        this.gateway.submitMiniActions(miniActions);
    }

    clicked(event: Event, item: ProductItem, rule: string): void {
        this.gateway.submitMiniActions([
            { featureId: BLACK_FRIDAY_SLIDER_FEATURE_ID, action: 'click', status: 'clicked' },
        ]);

        const trackingData = productClickLabels(event, item, rule, this.selectionEvents.current().entryRule);
        if (!trackingData) return;

        this.gateway.trackOnNextPageImpression(blackFridaySliderClickLabels(trackingData, item));
    }

    private reset(): void {
        this.reportedTiles.clear();
        this.reportedSlider = false;
    }

    private unreportedSliderActions(): MiniAction[] {
        if (this.reportedSlider) return [];

        this.reportedSlider = true;
        return [{ featureId: BLACK_FRIDAY_SLIDER_FEATURE_ID, action: 'scroll', status: 'visible' }];
    }

    private unreportedTileActions(tiles: ProductItem[]): MiniAction[] {
        return tiles
            .filter(({ localListPosition }) => !this.reportedTiles.has(localListPosition))
            .map(({ localListPosition }) => {
                this.reportedTiles.add(localListPosition);
                return { featureId: sliderTileFeatureId(localListPosition), action: 'scroll', status: 'visible' };
            });
    }
}
